import { Controller } from "@hotwired/stimulus"

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
]

function padDay(d) {
  return String(d).padStart(2, "0")
}

function formatDates(arrivalStr, departureStr) {
  if (!arrivalStr || !departureStr) return ""

  const a = new Date(arrivalStr + "T00:00:00")
  const d = new Date(departureStr + "T00:00:00")
  if (isNaN(a) || isNaN(d)) return ""

  const fullDate = (dt) => `${padDay(dt.getDate())} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`
  const dayMonth = (dt) => `${padDay(dt.getDate())} ${MONTHS[dt.getMonth()]}`

  if (a.getTime() === d.getTime()) return fullDate(a)
  if (a.getFullYear() !== d.getFullYear()) return `${fullDate(a)} \u2014 ${fullDate(d)}`
  if (a.getMonth() !== d.getMonth()) return `${dayMonth(a)} \u2014 ${fullDate(d)}`
  return `${padDay(a.getDate())} \u2014 ${fullDate(d)}`
}

const PENCIL_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 20.9998H13M2.5 21.4998L8.04927 19.3655C8.40421 19.229 8.58168 19.1607 8.74772 19.0716C8.8952 18.9924 9.0358 18.901 9.16804 18.7984C9.31692 18.6829 9.45137 18.5484 9.72028 18.2795L21 6.99982C22.1046 5.89525 22.1046 4.10438 21 2.99981C19.8955 1.89525 18.1046 1.89524 17 2.99981L5.72028 14.2795C5.45138 14.5484 5.31692 14.6829 5.20139 14.8318C5.09877 14.964 5.0074 15.1046 4.92823 15.2521C4.83911 15.4181 4.77085 15.5956 4.63433 15.9506L2.5 21.4998ZM2.5 21.4998L4.55812 16.1488C4.7054 15.7659 4.77903 15.5744 4.90534 15.4867C5.01572 15.4101 5.1523 15.3811 5.2843 15.4063C5.43533 15.4351 5.58038 15.5802 5.87048 15.8703L8.12957 18.1294C8.41967 18.4195 8.56472 18.5645 8.59356 18.7155C8.61877 18.8475 8.58979 18.9841 8.51314 19.0945C8.42545 19.2208 8.23399 19.2944 7.85107 19.4417L2.5 21.4998Z"/></svg>`
const PENCIL_ICON = `<span style="position:absolute; top:8px; right:8px; color: var(--primary-color, #16a34a); opacity:0.5; pointer-events:none; transition:opacity 0.15s;" data-pencil>${PENCIL_SVG}</span>`
const EDITABLE_STYLE = "position:relative; outline: none; border-radius: 8px; padding: 8px 28px 8px 8px; transition: box-shadow 0.15s, background 0.15s; cursor: text; border: 1px dashed rgba(0,0,0,0.15); background: rgba(0,0,0,0.015);"
const EDITABLE_HOVER = "this.style.borderColor='rgba(0,0,0,0.3)'; this.style.background='rgba(0,0,0,0.02)'; var p=this.querySelector('[data-pencil]'); if(p) p.style.opacity='0.8'"
const EDITABLE_LEAVE = "if(document.activeElement!==this){this.style.borderColor='rgba(0,0,0,0.15)'; this.style.background='rgba(0,0,0,0.015)'; var p=this.querySelector('[data-pencil]'); if(p) p.style.opacity='0.5'}"
const EDITABLE_FOCUS = "this.style.borderColor='var(--primary-color, #16a34a)'; this.style.borderStyle='solid'; this.style.background='white'; this.style.boxShadow='0 0 0 1px var(--primary-color, #16a34a)'; var p=this.querySelector('[data-pencil]'); if(p) p.style.opacity='0'"
const EDITABLE_BLUR = "this.style.borderColor='rgba(0,0,0,0.15)'; this.style.borderStyle='dashed'; this.style.background='rgba(0,0,0,0.015)'; this.style.boxShadow='none'; var p=this.querySelector('[data-pencil]'); if(p) p.style.opacity='0.5'"

export default class extends Controller {
  static values = {
    countryName: String,
    companyName: String,
    organizationName: String,
    ownerName: { type: String, default: "" },
    ownerTitle: { type: String, default: "" },
    logoUrl: { type: String, default: "" },
    defaultMessage: String,
    defaultGreeting: { type: String, default: "Dear %{traveller}," },
    subject: { type: String, default: "" },
    travellerName: { type: String, default: "" },
    travelDates: { type: String, default: "" }
  }

  static targets = ["textarea", "greetingField", "preview", "fileInput"]

  connect() {
    this._observer = new MutationObserver(() => {
      if (!this.element.classList.contains("hidden")) this._render()
    })
    this._observer.observe(this.element, { attributes: true, attributeFilter: ["class"] })
    this._render()
  }

  disconnect() {
    this._observer?.disconnect()
    this._teardownImageResize()
  }

  get _editable() {
    return this.element.querySelector("[data-editable='combined']")
  }

  editableChanged() {
    this._sync()
  }

  _sync() {
    const editable = this._editable
    if (!editable) return

    // Clone and strip UI elements
    const clone = editable.cloneNode(true)
    clone.querySelectorAll("[data-pencil], [data-resize-handle], [data-delete-btn]").forEach(p => p.remove())
    // Remove selection outlines from cloned images
    clone.querySelectorAll("img").forEach(img => { img.style.outline = ""; img.style.outlineOffset = "" })

    const html = clone.innerHTML

    // Split: first block element (or first line of text) is the greeting, rest is message
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = html

    // Extract greeting from the first text node or first element
    let greetingText = ""
    let messageHtml = ""

    const children = Array.from(tempDiv.childNodes)
    if (children.length > 0) {
      const first = children[0]
      if (first.nodeType === Node.TEXT_NODE) {
        // Plain text — split on first newline
        const lines = first.textContent.split("\n")
        greetingText = lines[0]
        const rest = lines.slice(1).join("\n").trim()
        // Rebuild: rest of first text node + remaining children
        const remaining = children.slice(1).map(n => n.outerHTML || n.textContent).join("")
        messageHtml = (rest ? rest + "\n" : "") + remaining
      } else {
        // First child is an element — use its text as greeting
        greetingText = first.textContent
        messageHtml = children.slice(1).map(n => n.outerHTML || n.textContent).join("")
      }
    }

    messageHtml = messageHtml.trim()

    const nameInput = document.querySelector("[name='certificate[traveller_name]']")
    const traveller = nameInput?.value || this.travellerNameValue || "Traveller"

    if (this.hasGreetingFieldTarget) {
      const rawGreeting = greetingText.replaceAll(traveller, "%{traveller}")
      this.greetingFieldTarget.value = rawGreeting
      this.greetingFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }

    if (this.hasTextareaTarget) {
      const rawMessage = messageHtml
        .replaceAll(this.companyNameValue, "%{company}")
        .replaceAll(this.countryNameValue, "%{country}")
      this.textareaTarget.value = rawMessage
      this.textareaTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  // ── Rich-text toolbar actions ──

  bold(event) {
    event?.preventDefault()
    document.execCommand("bold")
    this._sync()
  }

  italic(event) {
    event?.preventDefault()
    document.execCommand("italic")
    this._sync()
  }

  formatBlock(event) {
    event?.preventDefault()
    const tag = event.currentTarget.dataset.tag
    document.execCommand("formatBlock", false, tag)
    this._sync()
  }

  bulletList(event) {
    event?.preventDefault()
    document.execCommand("insertUnorderedList")
    this._sync()
  }

  insertLink(event) {
    event?.preventDefault()
    const url = prompt("Enter URL:")
    if (url) {
      document.execCommand("createLink", false, url)
      this._sync()
    }
  }

  insertImage(event) {
    event?.preventDefault()
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return

    this._compressImage(file).then((dataUrl) => {
      const editable = this._editable
      if (editable) editable.focus()
      document.execCommand("insertImage", false, dataUrl)
      // Constrain inserted images
      setTimeout(() => {
        editable?.querySelectorAll("img:not([style*='max-width'])").forEach(img => {
          img.style.maxWidth = "100%"
          img.style.height = "auto"
          img.style.borderRadius = "8px"
          img.style.margin = "8px 0"
          img.style.display = "block"
        })
        this._sync()
      }, 50)
    })
    event.target.value = ""
  }

  _compressImage(file, maxWidth = 800, quality = 0.7) {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement("canvas")
        let w = img.width
        let h = img.height

        if (w > maxWidth) {
          h = Math.round(h * (maxWidth / w))
          w = maxWidth
        }

        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, w, h)

        resolve(canvas.toDataURL("image/jpeg", quality))
        URL.revokeObjectURL(img.src)
      }
      img.src = URL.createObjectURL(file)
    })
  }

  insertPlaceholder(event) {
    event.preventDefault()
    const placeholder = event.currentTarget.dataset.placeholder
    const editable = this._editable
    if (!editable) return

    const nameInput = document.querySelector("[name='certificate[traveller_name]']")
    const traveller = nameInput?.value || this.travellerNameValue || "Traveller"

    const resolved = placeholder
      .replace(/%\{country\}/g, this.countryNameValue)
      .replace(/%\{company\}/g, this.companyNameValue)
      .replace(/%\{traveller\}/g, traveller)

    const sel = window.getSelection()
    if (sel.rangeCount && editable.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0)
      range.deleteContents()
      range.insertNode(document.createTextNode(resolved))
      range.collapse(false)
      sel.removeAllRanges()
      sel.addRange(range)
    } else {
      editable.textContent += resolved
    }
    this._sync()
    editable.focus()
  }

  update() {
    if (this._timer) clearTimeout(this._timer)
    this._timer = setTimeout(() => this._render(), 150)
  }

  _render() {
    if (!this.hasPreviewTarget) return

    const messageRaw = (this.hasTextareaTarget ? this.textareaTarget.value : "") || this.defaultMessageValue
    const resolved = messageRaw
      .replace(/%\{country\}/g, this.countryNameValue)
      .replace(/%\{company\}/g, this.companyNameValue)

    const greeting = (this.hasGreetingFieldTarget ? this.greetingFieldTarget.value : "") || this.defaultGreetingValue
    const nameInput = document.querySelector("[name='certificate[traveller_name]']")
    const traveller = nameInput?.value || this.travellerNameValue || "Traveller"
    const resolvedGreeting = greeting.replace(/%\{traveller\}/g, traveller)

    // The heading is the admin's, not the operator's — resolved the same way.
    const subject = this.subjectValue
      .replace(/%\{country\}/g, this.countryNameValue)
      .replace(/%\{company\}/g, this.companyNameValue)
      .replace(/%\{traveller\}/g, traveller)

    // Determine if message contains HTML
    const isHtml = /<[a-z][\s\S]*>/i.test(resolved)
    const messageContent = isHtml ? resolved : this._esc(resolved)

    const arrivalInput = document.querySelector("[name='certificate[arrival_date]']")
    const departureInput = document.querySelector("[name='certificate[departure_date]']")
    const dates = formatDates(arrivalInput?.value, departureInput?.value) || this.travelDatesValue

    const logoHtml = this.logoUrlValue
      ? `<div style="text-align: left; padding: 24px 0 0;"><img src="${this._esc(this.logoUrlValue)}" alt="${this._esc(this.companyNameValue)}" style="max-height: 80px; max-width: 100%; width: auto; height: auto;"></div>`
      : `<div style="text-align: left; padding: 24px 0 0;"><strong style="font-size: 18px;">${this._esc(this.companyNameValue)}</strong></div>`

    const ownerNameHtml = this.ownerNameValue
      ? `<p style="margin: 4px 0 0; font-size: 14px; font-weight: 600;">${this._esc(this.ownerNameValue)}</p>`
      : ""

    const ownerTitleHtml = this.ownerTitleValue
      ? `<p style="margin: 2px 0 0; font-size: 13px; color: #6b7280;">${this._esc(this.ownerTitleValue)}, ${this._esc(this.companyNameValue)}</p>`
      : ""

    this.previewTarget.innerHTML = `
      <div style="font-family: sans-serif; padding: 24px; background: white;">
        <div style="padding: 0 0 8px; border-bottom: 1px solid #e5e7eb; margin-bottom: 16px;">
          <h1 style="font-size: 22px; font-weight: 700; margin: 0; letter-spacing: 0.02em;">${this._esc(subject)}</h1>
        </div>
        <div contenteditable="true"
           data-editable="combined"
           data-action="input->email-preview#editableChanged"
           class="prose prose-sm max-w-none
                  [&_h2]:text-base [&_h2]:font-bold [&_h2]:mb-1
                  [&_h3]:text-sm [&_h3]:font-bold [&_h3]:mb-1
                  [&_p]:mb-2 [&_p:last-child]:mb-0
                  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:mb-0.5
                  [&_a]:text-blue-600 [&_a]:underline
                  [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-2"
           style="margin: 0 0 12px; font-size: 14px; ${EDITABLE_STYLE} min-height: 4em;"
           onmouseenter="${EDITABLE_HOVER}"
           onmouseleave="${EDITABLE_LEAVE}"
           onfocus="${EDITABLE_FOCUS}"
           onblur="${EDITABLE_BLUR}"
        ><p>${this._esc(resolvedGreeting)}</p>${messageContent}${PENCIL_ICON}</div>
        ${dates ? `<p style="margin: 0 0 12px; font-size: 14px;"><strong>Travel dates:</strong> ${this._esc(dates)}</p>` : ""}
        <p style="margin: 0 0 12px; font-size: 14px;">You can view your certificate online at any time:</p>
        <p style="margin: 0 0 12px;"><a style="color: #166534; font-weight: 600; font-size: 14px;">View Certificate</a></p>
        <p style="margin: 0 0 12px; font-size: 14px;">Your certificate as PDF and JPG is also attached to this email – ready to share with friends and family on social media.<br>Or print it, put it in a frame and hang it on the wall.</p>
        <p style="margin: 0 0 12px; font-size: 14px;">We'd love to hear about your ${this._esc(this.countryNameValue)} experience! Please take a moment to fill out our short questionnaire:</p>
        <p style="margin: 0 0 12px;"><a style="color: #166534; font-weight: 600; font-size: 14px;">Fill out questionnaire</a></p>
        <p style="margin: 0 0 0; font-size: 14px;">Thank you.</p>
        ${logoHtml}
        <div style="padding: 12px 0 0; text-align: left;">
          <p style="margin: 0; font-size: 14px;">Best regards,</p>
          ${ownerNameHtml}
          ${ownerTitleHtml}
        </div>
        
      </div>
    `
    this._setupImageResize()
  }

  // ── Image resize & delete ──

  _setupImageResize() {
    this._teardownImageResize()
    const editable = this._editable
    if (!editable) return

    this._onImgClick = (e) => {
      const img = e.target.closest("img")
      if (img && editable.contains(img)) {
        e.preventDefault()
        this._selectImage(img)
      } else if (!e.target.closest("[data-resize-handle]") && !e.target.closest("[data-delete-btn]")) {
        this._deselectImage()
      }
    }
    editable.addEventListener("click", this._onImgClick)

    this._onOutsideClick = (e) => {
      if (!editable.contains(e.target)) this._deselectImage()
    }
    document.addEventListener("click", this._onOutsideClick)

    // Delete key removes selected image
    this._onKeyDown = (e) => {
      if (this._selectedImg && (e.key === "Delete" || e.key === "Backspace")) {
        e.preventDefault()
        this._selectedImg.remove()
        this._deselectImage()
        this._sync()
      }
    }
    document.addEventListener("keydown", this._onKeyDown)
  }

  _teardownImageResize() {
    const editable = this._editable
    if (editable && this._onImgClick) editable.removeEventListener("click", this._onImgClick)
    if (this._onOutsideClick) document.removeEventListener("click", this._onOutsideClick)
    if (this._onKeyDown) document.removeEventListener("keydown", this._onKeyDown)
    this._deselectImage()
  }

  _selectImage(img) {
    this._deselectImage()
    this._selectedImg = img

    img.style.outline = "2px solid var(--primary-color, #16a34a)"
    img.style.outlineOffset = "2px"

    const editable = this._editable
    const pos = getComputedStyle(editable).position
    if (pos === "static") editable.style.position = "relative"

    // Resize handle (bottom-right)
    const handle = document.createElement("div")
    handle.setAttribute("data-resize-handle", "")
    handle.style.cssText = `
      position: absolute; width: 14px; height: 14px;
      background: var(--primary-color, #16a34a); border: 2px solid white;
      border-radius: 2px; cursor: nwse-resize; z-index: 10;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    `
    this._positionHandle(img, handle, editable)
    editable.appendChild(handle)
    this._resizeHandle = handle

    // Delete button (top-right of image)
    const del = document.createElement("button")
    del.setAttribute("data-delete-btn", "")
    del.type = "button"
    del.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round"><line x1="4" y1="4" x2="20" y2="20"/><line x1="20" y1="4" x2="4" y2="20"/></svg>`
    del.style.cssText = `
      position: absolute; width: 22px; height: 22px; z-index: 10;
      background: #ef4444; border: 2px solid white; border-radius: 50%;
      cursor: pointer; display: flex; align-items: center; justify-content: center;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3); padding: 0;
    `
    this._positionDeleteBtn(img, del, editable)
    editable.appendChild(del)
    this._deleteBtn = del

    del.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      img.remove()
      this._deselectImage()
      this._sync()
    })

    // Drag-to-resize
    let startX, startWidth
    const onMouseDown = (e) => {
      e.preventDefault()
      e.stopPropagation()
      startX = e.clientX
      startWidth = img.offsetWidth
      document.addEventListener("mousemove", onMouseMove)
      document.addEventListener("mouseup", onMouseUp)
    }
    const onMouseMove = (e) => {
      const dx = e.clientX - startX
      const newWidth = Math.max(50, startWidth + dx)
      const maxWidth = editable.clientWidth - 20
      img.style.width = Math.min(newWidth, maxWidth) + "px"
      img.style.height = "auto"
      this._positionHandle(img, handle, editable)
      this._positionDeleteBtn(img, del, editable)
    }
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
      this._sync()
    }
    handle.addEventListener("mousedown", onMouseDown)

    // Touch support
    handle.addEventListener("touchstart", (e) => {
      e.preventDefault()
      e.stopPropagation()
      const touch = e.touches[0]
      startX = touch.clientX
      startWidth = img.offsetWidth
      const onTouchMove = (e) => {
        const t = e.touches[0]
        const dx = t.clientX - startX
        const newWidth = Math.max(50, startWidth + dx)
        const maxWidth = editable.clientWidth - 20
        img.style.width = Math.min(newWidth, maxWidth) + "px"
        img.style.height = "auto"
        this._positionHandle(img, handle, editable)
        this._positionDeleteBtn(img, del, editable)
      }
      const onTouchEnd = () => {
        document.removeEventListener("touchmove", onTouchMove)
        document.removeEventListener("touchend", onTouchEnd)
        this._sync()
      }
      document.addEventListener("touchmove", onTouchMove)
      document.addEventListener("touchend", onTouchEnd)
    })
  }

  _positionHandle(img, handle, editable) {
    const eRect = editable.getBoundingClientRect()
    const iRect = img.getBoundingClientRect()
    handle.style.top = (iRect.bottom - eRect.top - 7) + "px"
    handle.style.left = (iRect.right - eRect.left - 7) + "px"
  }

  _positionDeleteBtn(img, btn, editable) {
    const eRect = editable.getBoundingClientRect()
    const iRect = img.getBoundingClientRect()
    btn.style.top = (iRect.top - eRect.top - 8) + "px"
    btn.style.left = (iRect.right - eRect.left - 14) + "px"
  }

  _deselectImage() {
    if (this._selectedImg) {
      this._selectedImg.style.outline = ""
      this._selectedImg.style.outlineOffset = ""
      this._selectedImg = null
    }
    if (this._resizeHandle) { this._resizeHandle.remove(); this._resizeHandle = null }
    if (this._deleteBtn) { this._deleteBtn.remove(); this._deleteBtn = null }
  }

  _esc(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
